/**
 * On-screen button bar for touch devices (iPhone / iPad) — dispatches synthetic
 * keydown/keyup events so every existing key handler (Input, KeyHelp,
 * MagnifyingGlass, BugSpray) works without modification.
 *
 * Always visible — the hover-none media query is unreliable on hybrid devices.
 * On desktops it's a useful cheat-sheet / one-handed control anyway.
 */

interface Button {
  label: string;
  key?: string;
  /** If true, keydown on press + keyup on release (for held keys like B). */
  held?: boolean;
  /** Tooltip / long-name. */
  title?: string;
  /** Custom tap action (overrides key dispatch). */
  onTap?: () => void;
}

interface TouchControlsDeps {
  /** Called when the colony / caste chart button is tapped. */
  toggleColonyHud?: () => void;
}

// Emoji-only labels on mobile so the bar stays compact at small screen widths.
// Title attribute still carries the long-name for desktop tooltip hover.
const BUTTONS: Button[] = [
  { label: '👥5',  key: '1', title: 'Summon 5 followers' },
  { label: '👥15', key: '2', title: 'Summon 15 followers' },
  { label: '½',    key: '3', title: 'Release half' },
  { label: '⏏︎',  key: '4', title: 'Release all' },
  { label: '🏠',   key: 'h', title: 'Send player home' },
  { label: '🎯',   key: 'g', title: 'Fly camera to player' },
  { label: '🎥',   key: 'f', title: 'Toggle follow-cam' },
  { label: '👁',   key: 'v', title: 'First-person ant cam' },
  { label: '🔥',   key: 'm', title: 'Magnifying glass' },
  { label: '💨',   key: 'b', held: true, title: 'Bug spray (hold)' },
  { label: '⌨',   key: 'k', title: 'Key commands' },
];

export function buildTouchControls(deps: TouchControlsDeps = {}): void {
  // Only show on iPhone / iPad. iPadOS 13+ reports Mac UA but sets maxTouchPoints
  // so we check both. Desktop users have the keyboard and don't need the bar.
  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1);
  if (!isIOS) return;
  const buttons: Button[] = [...BUTTONS];
  if (deps.toggleColonyHud) {
    buttons.push({ label: '📊', title: 'Colony chart', onTap: deps.toggleColonyHud });
  }

  const bar = document.createElement('div');
  bar.id = 'touch-controls';
  bar.style.cssText = [
    'position:fixed',
    'bottom:8px',
    'left:50%',
    'transform:translateX(-50%)',
    'display:flex',
    'gap:6px',
    'flex-wrap:wrap',
    'justify-content:center',
    'max-width:calc(100vw - 16px)',
    'padding:6px 8px',
    'background:rgba(15,20,25,0.78)',
    'border-radius:10px',
    'box-shadow:0 2px 8px rgba(0,0,0,0.4)',
    'z-index:8',
    'pointer-events:auto',
    '-webkit-user-select:none',
    'user-select:none',
  ].join(';');

  for (const b of buttons) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = b.label;
    if (b.title) btn.title = b.title;
    btn.style.cssText = [
      'background:rgba(255,255,255,0.08)',
      'color:#eee',
      'border:1px solid rgba(255,255,255,0.15)',
      'border-radius:6px',
      'padding:8px 10px',
      'min-width:44px',
      'min-height:36px',
      'font:11px/1 system-ui,sans-serif',
      'cursor:pointer',
      'touch-action:manipulation',
    ].join(';');

    const fireDown = () => { if (b.key) window.dispatchEvent(new KeyboardEvent('keydown', { key: b.key })); };
    const fireUp = () => { if (b.key) window.dispatchEvent(new KeyboardEvent('keyup', { key: b.key })); };

    if (b.onTap) {
      btn.addEventListener('pointerup', (e) => {
        e.preventDefault();
        b.onTap!();
      });
    } else if (b.held) {
      // Press-and-hold: keydown on start, keyup on release / cancel. Used for
      // the bug-spray cone which wants a continuous emit while finger is down.
      const start = (e: Event) => { e.preventDefault(); fireDown(); };
      const end = (e: Event) => { e.preventDefault(); fireUp(); };
      btn.addEventListener('pointerdown', start);
      btn.addEventListener('pointerup', end);
      btn.addEventListener('pointerleave', end);
      btn.addEventListener('pointercancel', end);
    } else {
      // One-shot tap: dispatch a full keydown+keyup pair on pointerup so
      // keyboard-triggered logic (toggles, summons) runs exactly once.
      btn.addEventListener('pointerup', (e) => {
        e.preventDefault();
        fireDown();
        fireUp();
      });
    }

    bar.appendChild(btn);
  }

  document.body.appendChild(bar);
}
